import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Badge, Box, Group, Text, Tooltip } from '@mantine/core'
import type { ProjectTagRow } from '@/types/api'
import { normalizeTagColorHex, ticketTagChipStyle } from '@/lib/ticket-tag-ui'

/**
 * Метки под темой в списке обращений: как чипы на карточке, не влезающие — «+N», tooltip со списком скрытых.
 */
export function TicketListTagsRow({
  tagIds,
  projectTags,
}: {
  tagIds: string[]
  projectTags: ProjectTagRow[]
}) {
  const byId = useMemo(() => new Map(projectTags.map((t) => [t.id, t])), [projectTags])

  const orderedTags = useMemo(
    () => tagIds.map((id) => byId.get(id)).filter((t): t is ProjectTagRow => Boolean(t)),
    [tagIds, byId],
  )

  const widthSlotRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(() => orderedTags.length)

  const recomputeVisible = useCallback(() => {
    const slot = widthSlotRef.current
    const measureEl = measureRef.current
    if (!slot) return
    const W = slot.clientWidth
    const n = orderedTags.length
    if (W <= 0 || n === 0) {
      setVisibleCount(n)
      return
    }

    const chipEls = measureEl?.querySelectorAll<HTMLElement>('[data-measure-tag-chip]')
    const widths =
      chipEls && chipEls.length === n
        ? [...chipEls].map((el) => el.getBoundingClientRect().width)
        : orderedTags.map(() => 88)

    const moreEl = measureEl?.querySelector<HTMLElement>('[data-measure-more]')
    const moreW = moreEl?.getBoundingClientRect().width ?? 40

    const gs = 6
    let best = n
    for (let v = n; v >= 0; v--) {
      const hidden = n - v
      let used = 0
      for (let j = 0; j < v; j++) {
        used += (widths[j] ?? 72) + (j > 0 ? gs : 0)
      }
      if (hidden > 0) used += (v > 0 ? gs : 0) + moreW
      if (used <= W) {
        best = v
        break
      }
    }
    setVisibleCount(best)
  }, [orderedTags])

  useLayoutEffect(() => {
    recomputeVisible()
  }, [recomputeVisible])

  useEffect(() => {
    const el = widthSlotRef.current
    if (!el) return
    const ro = new ResizeObserver(() => recomputeVisible())
    ro.observe(el)
    return () => ro.disconnect()
  }, [recomputeVisible])

  if (orderedTags.length === 0) return null

  const visibleTags = orderedTags.slice(0, visibleCount)
  const hiddenTags = orderedTags.slice(visibleCount)
  const hiddenLabel =
    hiddenTags.length > 0 ? hiddenTags.map((t) => `«${t.name}»`).join(', ') : ''

  const chipInner = (tag: ProjectTagRow) => {
    const hex = normalizeTagColorHex(tag.color)
    return (
      <>
        <Box
          style={{
            width: 5,
            height: 5,
            borderRadius: 999,
            backgroundColor: hex,
            flexShrink: 0,
          }}
        />
        <Text component="span" fw={500} truncate style={{ minWidth: 0, fontSize: 11, lineHeight: 1.2 }}>
          {tag.name}
        </Text>
      </>
    )
  }

  return (
    <Box
      ref={widthSlotRef}
      style={{
        minWidth: 0,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <Box
        ref={measureRef}
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          whiteSpace: 'nowrap',
          zIndex: -1,
        }}
      >
        {orderedTags.map((tag) => (
          <Box
            key={tag.id}
            data-measure-tag-chip
            style={{ ...ticketTagChipStyle(normalizeTagColorHex(tag.color)), maxWidth: 176 }}
          >
            {chipInner(tag)}
          </Box>
        ))}
        <Badge data-measure-more size="xs" variant="light" color="gray" style={{ flexShrink: 0 }}>
          +{orderedTags.length}
        </Badge>
      </Box>

      <Group wrap="nowrap" gap={6} align="center" style={{ minWidth: 0, overflow: 'hidden', width: '100%' }}>
        {visibleTags.map((tag) => {
          const hex = normalizeTagColorHex(tag.color)
          return (
            <Box
              key={tag.id}
              className="ticket-tag-chip ticket-tag-chip--readonly"
              style={{ ...ticketTagChipStyle(hex), flexShrink: 0 }}
            >
              {chipInner(tag)}
            </Box>
          )
        })}
        {hiddenTags.length > 0 ? (
          <Tooltip label={hiddenLabel} multiline maw={320} withArrow position="bottom">
            <Badge size="xs" variant="light" color="gray" style={{ flexShrink: 0, cursor: 'default' }}>
              +{hiddenTags.length}
            </Badge>
          </Tooltip>
        ) : null}
      </Group>
    </Box>
  )
}
